import { User } from './types';
import { apiCall } from '@src/services/apiCall';
import { getJsonFromLocalStorage } from '@src/services/storage';
import {
  createCustomBackgroundUrl,
  CUSTOM_BACKGROUND_BLOB_KEY,
  dataUrlToBlob,
  deleteCustomBackgroundBlob,
  getCustomBackgroundBlob,
  saveCustomBackgroundBlob,
} from '@src/services/customBackgroundStorage';

const BACKGROUND_HISTORY_KEY = 'backgroundHistory';
const MAX_BACKGROUND_HISTORY = 12;
const DEFAULT_BACKGROUND_RAW_URL = 'https://ik.imagekit.io/tqaevu6rl/wallpapers/preset_forest_1_YumnoXte3l';
const DEFAULT_BACKGROUND_THUMB_URL = `${DEFAULT_BACKGROUND_RAW_URL}?tr=w-300,h-200,fo-auto`;
export const CUSTOM_BG_KEY = 'customBackground';

export interface WalpaperType {
  raw: string;
  thumb: string;
  isGradient?: boolean;
  isAnimated?: boolean;
  isCustom?: boolean;
  customType?: 'image' | 'video';
  customFile?: string; // Legacy Base64 field; migrated to IndexedDB on first load.
  customStorageKey?: string;
  type: 'image' | 'gradient' | 'animated' | 'custom';
  animation?: {
    type: 'cloud' | 'other';
    config?: Record<string, unknown>;
  };
  remoteUrl?: string;
}

export const preloadImage = (src: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(src);
      img.onerror = reject;
    });
};

// Upgrade the low-resolution default URL stored by versions that used the
// 300x200 thumbnail as the full-screen wallpaper.
export const normalizeBackground = (background: WalpaperType): WalpaperType => {
  if (background.type === 'image' && background.raw === DEFAULT_BACKGROUND_THUMB_URL) {
    return {
      ...background,
      raw: DEFAULT_BACKGROUND_RAW_URL,
      thumb: DEFAULT_BACKGROUND_THUMB_URL,
    };
  }

  return background;
};

export const getWallpapers = async (): Promise<WalpaperType[]> => {
    const storedHistory = getJsonFromLocalStorage<unknown>(BACKGROUND_HISTORY_KEY, []);
    const history = Array.isArray(storedHistory)
      ? (storedHistory as WalpaperType[]).map(normalizeBackground)
      : [];
    if (Array.isArray(storedHistory) && history.some((background, index) => background !== storedHistory[index])) {
      localStorage.setItem(BACKGROUND_HISTORY_KEY, JSON.stringify(history));
    }
    const customData = getJsonFromLocalStorage<WalpaperType | WalpaperType[] | null>(CUSTOM_BG_KEY, null);
    const storedCustom = Array.isArray(customData) ? customData[0] : customData;
    const custom = storedCustom ? await hydrateCustomBackground(storedCustom) : null;
    const defaultBackground = getDefaultBackground();
    
    // 只保留普通图片背景
    const merged = history.filter((bg: WalpaperType) => 
      bg.type === 'image' && !bg.isCustom && !bg.isAnimated
    );
    
    // 检查历史记录中是否已包含默认背景
    const hasDefaultBackground = merged.some((bg: WalpaperType) => bg.raw === defaultBackground.raw);
    if (!hasDefaultBackground) {
        merged.push(defaultBackground);
    }

    // 确保返回包含了您想要的两张 Unsplash 图片
    const wallpapers = [...merged];
    
    // 如果还没有这两张图片，添加它们
    const unsplashImage1: WalpaperType = {
      raw: DEFAULT_BACKGROUND_RAW_URL,
      thumb: DEFAULT_BACKGROUND_THUMB_URL,
      type: "image"
    };

    const unsplashImage2: WalpaperType = {
      raw: "https://ik.imagekit.io/tqaevu6rl/wallpapers/T7K4aEPoGGk_VzZePSvqq",
      thumb: "https://ik.imagekit.io/tqaevu6rl/wallpapers/T7K4aEPoGGk_VzZePSvqq?tr=w-300,h-200,fo-auto",
      type: "image"
    };
    
    if (!wallpapers.some(wp => wp.raw === unsplashImage1.raw)) {
      wallpapers.push(unsplashImage1);
      // 预加载缩略图
      preloadImage(unsplashImage1.thumb).catch(() => {});
    }
    
    if (!wallpapers.some(wp => wp.raw === unsplashImage2.raw)) {
      wallpapers.push(unsplashImage2);
      // 预加载缩略图
      preloadImage(unsplashImage2.thumb).catch(() => {});
    }
    
    return custom ? [custom, ...wallpapers] : wallpapers;
}

export const getDefaultBackground = (): WalpaperType => {
  return {
    raw: DEFAULT_BACKGROUND_RAW_URL,
    thumb: DEFAULT_BACKGROUND_THUMB_URL,
    type: "image"
  };
};

export const serializeBackground = (background: WalpaperType): WalpaperType => {
  if (background.type !== 'custom') return background;

  return {
    ...background,
    raw: background.remoteUrl ?? '',
    customFile: undefined,
  };
};

export const hydrateCustomBackground = async (background: WalpaperType): Promise<WalpaperType> => {
  if (background.type !== 'custom') return background;
  if (background.remoteUrl) return { ...background, raw: background.remoteUrl };

  let storageKey = background.customStorageKey;
  let blob = storageKey ? await getCustomBackgroundBlob(storageKey) : null;

  // One-time migration for versions that stored the complete file as Base64.
  if (!blob && background.customFile?.startsWith('data:')) {
    blob = dataUrlToBlob(background.customFile);
    storageKey = await saveCustomBackgroundBlob(blob);
  }

  if (!blob) throw new Error('Custom background file is unavailable');

  const hydrated = {
    ...background,
    raw: createCustomBackgroundUrl(blob),
    customFile: undefined,
    customStorageKey: storageKey ?? CUSTOM_BACKGROUND_BLOB_KEY,
  };

  localStorage.setItem(CUSTOM_BG_KEY, JSON.stringify([serializeBackground(hydrated)]));
  return hydrated;
};

export const loadStoredCustomBackground = async (): Promise<WalpaperType | null> => {
  const stored = getJsonFromLocalStorage<WalpaperType | WalpaperType[] | null>(CUSTOM_BG_KEY, null);
  const background = Array.isArray(stored) ? stored[0] : stored;
  return background ? hydrateCustomBackground(background) : null;
};

export const removeStoredCustomBackground = async () => {
  localStorage.removeItem(CUSTOM_BG_KEY);
  await deleteCustomBackgroundBlob().catch(error => {
    console.warn('Unable to remove custom background media', error);
  });
};

export const getNextBackground = async (): Promise<WalpaperType> => {
    const fetchNewWallpaper = async (): Promise<WalpaperType> => {
        const data = await apiCall.get('/unsplash/wallpaper', {
            query: 'landscape',
            count: 1,
            orientation: 'landscape',
            order_by: 'latest'
        });

        // 根据API实际返回结构调整，假设返回结构保持不变
        return {
            raw: data[0].raw, 
            thumb: data[0].thumb,
            type: 'image'
        };
    };

    try {
        let newWallpaper = getDefaultBackground();
        let attempts = 0;
        const maxAttempts = 3;
        let wallpapers = await getWallpapers();
        let isNewBackground = true;
        
        while (attempts < maxAttempts) {
            try {
                newWallpaper = await fetchNewWallpaper();
            } catch (error) {
                console.error('API call failed, using default background:', error);
                newWallpaper = getDefaultBackground();
                isNewBackground = false;
                break;
            }
            
            attempts++;
            const isExisting = wallpapers.some(wp => wp.raw === newWallpaper.raw);
            if (!isExisting || attempts >= maxAttempts) {
                break;
            }
        }

        wallpapers = updateWallpapers(newWallpaper, wallpapers, isNewBackground);
        localStorage.setItem(BACKGROUND_HISTORY_KEY, JSON.stringify(wallpapers));
        return newWallpaper;
    } catch (error) {
        console.error('Error fetching background image:', error);
        return getDefaultBackground();
    }
};

export const updateWallpapers = (newWallpaper: WalpaperType, currentWallpapers: WalpaperType[], isNewBackground: boolean): WalpaperType[] => {
  const defaultBackground = getDefaultBackground();
  let updatedWallpapers: WalpaperType[];

  // 如果不是普通图片类型,直接返回当前列表
  if (newWallpaper.type !== 'image' || newWallpaper.isCustom || newWallpaper.isAnimated) {
    return currentWallpapers;
  }

  if (isNewBackground) {
    updatedWallpapers = [newWallpaper, ...currentWallpapers.filter(wp => wp.raw !== newWallpaper.raw)];
  } else {
    updatedWallpapers = currentWallpapers.map(wp => wp.raw === newWallpaper.raw ? newWallpaper : wp);
  }

  // 确保默认背景存在
  if (!updatedWallpapers.some(wp => wp.raw === defaultBackground.raw)) {
    updatedWallpapers.push(defaultBackground);
  }

  // 限制历史记录数量
  if (updatedWallpapers.length > MAX_BACKGROUND_HISTORY) {
    const nonDefaultBackgrounds = updatedWallpapers.filter(wp => 
      wp.raw !== defaultBackground.raw && wp.type === 'image' && !wp.isCustom && !wp.isAnimated
    );
    const defaultBackgrounds = updatedWallpapers.filter(wp => wp.raw === defaultBackground.raw);
    const keptNonDefaultBackgrounds = nonDefaultBackgrounds.slice(0, MAX_BACKGROUND_HISTORY - 1);
    updatedWallpapers = [...keptNonDefaultBackgrounds, defaultBackgrounds[0]];
  }
  return updatedWallpapers;
};

export const gradientColors = [
  { 
    id: 'gradient-1', 
    name: 'Ocean', 
    value: 'linear-gradient(62deg, rgb(142, 197, 252), rgb(224, 195, 252))' 
  },
  { 
    id: 'gradient-2', 
    name: 'Peach', 
    value: 'linear-gradient(-135deg, rgb(252, 203, 144), rgb(213, 126, 235))' 
  }
];

export const getRandomGradient = (currentGradient?: string): WalpaperType | null => {
  const availableGradients = gradientColors.filter(g => g.value !== currentGradient);
  if (availableGradients.length === 0) return null;
  const randomGradient = availableGradients[Math.floor(Math.random() * availableGradients.length)];
  return {
    raw: randomGradient.value,
    thumb: randomGradient.value,
    isGradient: true,
    type: 'gradient'
  };
};

// 修改 handleCustomUpload 方法
export const handleCustomUpload = async (file: File, user: User): Promise<WalpaperType> => {
  if (!['pro', 'premium'].includes(user.plan_name.toLowerCase())) {
    throw new Error('Only subscribed users can upload custom backgrounds');
  }

  const isVideo = file.type.startsWith('video/');
  if (file.size > 14.5 * 1024 * 1024) {
    throw new Error('Due to browser limitations, the file must be smaller than 14.5MB');
  }

  try {
    const [thumb, storageKey] = await Promise.all([
      generateThumbnail(file),
      saveCustomBackgroundBlob(file),
    ]);
    const customBg: WalpaperType = {
      raw: createCustomBackgroundUrl(file),
      thumb,
      isCustom: true,
      customType: isVideo ? 'video' : 'image',
      type: 'custom',
      customStorageKey: storageKey,
    };

    localStorage.setItem(CUSTOM_BG_KEY, JSON.stringify([serializeBackground(customBg)]));
    return customBg;
  } catch (error) {
    console.error('Error uploading custom background:', error);
    throw error;
  }
};

// 生成缩略图（通用方法）
const generateThumbnail = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const releaseObjectUrl = () => URL.revokeObjectURL(objectUrl);

    if (file.type.startsWith('image/')) {
      const img = new Image();
      img.src = objectUrl;
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = (img.height / img.width) * 400;
          canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL());
        } finally {
          releaseObjectUrl();
        }
      };
      img.onerror = () => {
        releaseObjectUrl();
        reject(new Error('Failed to load image for thumbnail generation'));
      };
    } else {
      // 视频截取第一帧
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.src = objectUrl;
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = (video.videoHeight / video.videoWidth) * 400;
        video.currentTime = Math.min(1, Math.max(video.duration / 2, 0));
        video.addEventListener('seeked', () => {
          try {
            canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL());
          } finally {
            releaseObjectUrl();
          }
        }, { once: true });
      }, { once: true });
      video.addEventListener('error', () => {
        releaseObjectUrl();
        reject(new Error('Failed to load video for thumbnail generation'));
      }, { once: true });
    }
  });
};

export const detectMediaType = async (url: string): Promise<'video' | 'image'> => {
  try {
    // 尝试发送HEAD请求检查内容类型
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.startsWith('video/')) {
      return 'video';
    } else if (contentType && contentType.startsWith('image/')) {
      return 'image';
    }
    
    // 如果无法从内容类型判断，尝试从URL判断
    if (/\.(mp4|webm|mov|avi|mkv)/i.test(url.split('?')[0])) {
      return 'video';
    }
    
    // 默认返回图片类型
    return 'image';
  } catch (error) {
    console.error('Error detecting media type:', error);
    // 如果检测失败，根据URL做简单判断
    if (/\.(mp4|webm|mov|avi|mkv)/i.test(url.split('?')[0])) {
      return 'video';
    }
    return 'image';
  }
};
